import { createReducer } from 'redux-act';
import Immutable from 'seamless-immutable';
import _ from 'lodash';

import { FileUploaderStore } from 'console-kit';

import StorageManager from '../../utils/StorageManager';
import CampaignPromotionActions from './promotion-actions';
import { campaignPromotionListSortBy, PROMOTION_PANELS, PROMOTION_ASSET_UPLOADER_PREFIX } from './promotion-enums';

const initialState = Immutable({
  byId:                         {},
  isFetching:                   false,
  hasFetched:                   false,
  sortListBy:                   campaignPromotionListSortBy.CURRENT_FUTURE_PAST,
  confirmDeleteVisible:         false,
  activePromotion:              {},
  promotionsSnapshot:           {}, //this will hold a byId snapshot while the editor is opened. Can be used to restore byId when cancelling from the editor
  isEditing:                    false,
  isSaving:                     false,
  isDiscardChangesModalVisible: false,
  isCloneModalVisible:          false,
  selectedPromotionToClone:     {},
  activeEditPanel:              PROMOTION_PANELS.SETTINGS,
  successMessage:               null,
});

const reducer = createReducer((on) => {
  //////////////////////////////////////////
  //         FETCHING PROMOTIONS          //
  //////////////////////////////////////////

  on(CampaignPromotionActions.fetchPromotions, (state, { isMicroExperience }) => {
    // initial fetch (using default sorting or the one stored in localStorage)
    // (RANDOM is not supported in NextGen)
    const storedSort = StorageManager.get('ngx_customUserPromotionSort');
    const _sort =
      storedSort && !(storedSort === campaignPromotionListSortBy.RANDOM && isMicroExperience)
        ? storedSort
        : initialState.sortListBy;
    return state.merge({ isFetching: true, sortListBy: _sort });
  });

  on(CampaignPromotionActions.fetchPromotionsSuccess, (state, { promotions }) => {
    const _byId = _.reduce(
      promotions,
      (acc, v, k) => {
        acc[v.id] = v;
        return acc;
      },
      {}
    );

    return state.merge({ byId: _byId, hasFetched: true, isFetching: false });
  });

  on(CampaignPromotionActions.fetchPromotionsFailure, (state) => state.set('isFetching', false));

  on(CampaignPromotionActions.sortPromotions, (state, { sort }) => {
    StorageManager.set('ngx_customUserPromotionSort', sort);
    return state.set('sortListBy', sort);
  });

  //////////////////////////////////////////
  //         PUBLISHING PROMOTIONS        //
  //////////////////////////////////////////

  on(CampaignPromotionActions.publishPromotionSuccess, (state, { promotion, successMessage }) =>
    state.setIn(['byId', promotion.id], promotion).set('successMessage', successMessage)
  );

  on(CampaignPromotionActions.draftPromotionSuccess, (state, { promotion, successMessage }) =>
    state.setIn(['byId', promotion.id], promotion).set('successMessage', successMessage)
  );

  //////////////////////////////////////////
  //         EDITING PROMOTIONS           //
  //////////////////////////////////////////

  on(CampaignPromotionActions.editPromotion, (state, { id }) =>
    state.set('activePromotion', state.byId[id]).set('isEditing', true)
  );

  on(CampaignPromotionActions.cancelPromotion, (state) => {
    FileUploaderStore.removeFiles(PROMOTION_ASSET_UPLOADER_PREFIX);

    return state
      .set('isEditing', false)
      .set('isSaving', false)
      .set('activePromotion', {})
      .set('byId', state.promotionsSnapshot)
      .set('isDiscardChangesModalVisible', false);
  });

  on(CampaignPromotionActions.addPromotionTemplateToTheList, (state, { promotionTemplate }) => {
    let newById = state.byId;
    newById = newById.set(promotionTemplate.id, promotionTemplate);

    return state.set('isEditing', true).set('activePromotion', promotionTemplate).set('byId', newById);
  });

  // Save promotions
  on(CampaignPromotionActions.persistPromotionsSuccess, (state, { promotions, successMessage, closeEditor }) => {
    if (closeEditor) {
      FileUploaderStore.removeFiles(PROMOTION_ASSET_UPLOADER_PREFIX);
    }

    let newById = state.byId;
    let newlyAddedPromotion = null;

    // Update/add promotion to byId
    _.each(promotions, (promotion) => {
      // If this promotion wasn't in byId before, it's newly added
      if (!state.byId[promotion.id]) {
        newlyAddedPromotion = promotion;
      }
      newById = newById.set(promotion.id, promotion);
    });

    // Remove any null key if it exists
    if (newById.null) {
      newById = newById.without('null');
    }

    const activePromotionId =
      state.activePromotion.id === null && newlyAddedPromotion ? newlyAddedPromotion.id : state.activePromotion.id;

    return state.merge({
      isSaving:                     false,
      successMessage,
      ...(closeEditor ? { isEditing: false } : {}),
      byId:                         newById,
      activePromotion:              closeEditor ? {} : newById[activePromotionId],
      promotionsSnapshot:           newById,
      isDiscardChangesModalVisible: false,
    });
  });

  on(CampaignPromotionActions.persistPromotionsFailure, (state) => state.set('isSaving', false));

  //////////////////////////////////////////
  //         DELETEING PROMOTIONS         //
  //////////////////////////////////////////

  on(CampaignPromotionActions.showConfirmDeleteMessage, (state) => {
    return state.set('confirmDeleteVisible', true);
  });

  on(CampaignPromotionActions.hideConfirmDeleteMessage, (state) => {
    return state.set('confirmDeleteVisible', false);
  });

  on(CampaignPromotionActions.deletePromotionSuccess, (state, { id, successMessage }) => {
    return state.merge({
      byId:                 state.byId.without(id),
      confirmDeleteVisible: false,
      successMessage,
    });
  });

  on(CampaignPromotionActions.deletePromotionFailure, (state, { id }) => state.set('confirmDeleteVisible', false));

  on(CampaignPromotionActions.openPromotionEditor, (state) => state.set('activeEditPanel', PROMOTION_PANELS.SETTINGS));

  on(CampaignPromotionActions.takePromotionsSnapshot, (state) => state.set('promotionsSnapshot', state.byId));

  // Set active edit panel
  on(CampaignPromotionActions.setActiveEditPanel, (state, { panel }) => state.set('activeEditPanel', panel));

  // Show/hide discard changes modal
  on(CampaignPromotionActions.setVisibilityDiscardChangesModal, (state, isVisible) =>
    state.set('isDiscardChangesModalVisible', isVisible)
  );

  // Show/hide clone modal
  on(CampaignPromotionActions.toggleClonePromotionModal, (state, { id }) => state.set('isCloneModalVisible', !!id));

  on(CampaignPromotionActions.setCloningPromotion, (state, { promotion }) =>
    state.set('selectedPromotionToClone', promotion)
  );

  on(CampaignPromotionActions.clonePromotionSuccess, (state, { promotion }) =>
    state.setIn(['byId', promotion.id], promotion)
  );

  on(CampaignPromotionActions.changePromotion, (state, { key, value }) => state.setIn(['activePromotion', key], value));

  on(CampaignPromotionActions.changePromotionDisplayStrategy, (state, { value }) =>
    state.setIn(['activePromotion', 'displayStrategy', 'id'], value)
  );

  on(CampaignPromotionActions.changePromotionDetailStrategy, (state, { value }) =>
    state.setIn(['activePromotion', 'detailStrategy', 'id'], value)
  );

  on(CampaignPromotionActions.updatePromotionsLayoutConfigs, (state, { layoutModels }) => {
    let newById = state.byId;

    _.each(layoutModels, (layoutModel) => {
      if (layoutModel.id) {
        newById = newById.setIn([layoutModel.id, 'layoutConfig'], layoutModel.layoutConfig);
      }
    });

    return state.set('byId', newById);
  });

  on(CampaignPromotionActions.updatePromotionsLayoutConfigsSuccess, (state, { successMessage }) =>
    state.merge({
      successMessage,
      promotionsSnapshot:           state.byId,
      isDiscardChangesModalVisible: false,
    })
  );

  //saves the actively edited promotion into the state
  on(CampaignPromotionActions.savePromotion, (state, { promotion }) => state.setIn(['byId', promotion.id], promotion));

  on(CampaignPromotionActions.dismissSuccessMessage, (state) =>
    state.set('successMessage', initialState.successMessage)
  );
}, initialState);

export default reducer;
